import React from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, Info, HelpCircle, Book, Mail, User } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 h-96 flex items-center bglog">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl text-white">
            <h1 className="text-4xl font-bold mb-4">Streamline your building approval process</h1>
            <p className="text-xl mb-8">Get your building designs approved faster through our digital platform</p>
            <div className="space-x-4">
              <Link to="/login" className="bg-white text-blue-700 py-2 px-6 rounded-md font-medium hover:bg-gray-100 transition duration-300">
                Login
              </Link>
              <Link to="/register" className="bg-transparent border border-white text-white py-2 px-6 rounded-md font-medium hover:bg-white hover:text-blue-700 transition duration-300">
                Register
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-16 bg-white clip-triangle"></div>
      </section>

      {/* Features Section */}
      <section className="py-16 container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition duration-300">
            <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <FileCheck className="text-blue-700" size={24} />
            </div>
            <h3 className="text-xl font-semibold mb-2">Submit Your Designs</h3>
            <p className="text-gray-600">Upload your PDF drawings and 3D IFC models for review.</p>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition duration-300">
            <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <HelpCircle className="text-blue-700" size={24} />
            </div>
            <h3 className="text-xl font-semibold mb-2">Get Approved</h3>
            <p className="text-gray-600">Our specialized approvers will review your designs across multiple levels.</p>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition duration-300">
            <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <FileCheck className="text-blue-700" size={24} />
            </div>
            <h3 className="text-xl font-semibold mb-2">Receive Certificate</h3>
            <p className="text-gray-600">Get your final Certificate of Approval for your building designs.</p>
          </div>
        </div>
      </section>

      {/* 3D Model Preview Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-6 md:mb-0">
              <h2 className="text-3xl font-bold mb-4">Advanced 3D Visualization</h2>
              <p className="text-gray-600 mb-4">
                Our platform leverages cutting-edge IFC.js technology to render your 3D models with precision.
                Approvers can interact with your designs, selecting objects and adding comments for a streamlined
                review process.
              </p>
              <Link to="/how-it-works" className="text-blue-700 font-medium hover:text-blue-800 transition duration-300 flex items-center">
                Learn more <span className="ml-2">→</span>
              </Link>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <div className="w-full max-w-md h-64 bg-blue-900 rounded-lg flex items-center justify-center text-white">
                [3D Model Preview Placeholder]
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-12">Quick Links</h2>
        <div className="grid md:grid-cols-4 gap-6">
          <Link to="/about" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition duration-300">
            <Info size={32} className="text-blue-700 mb-4" />
            <span className="text-lg font-medium">About Us</span>
          </Link>
          <Link to="/how-it-works" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition duration-300">
            <HelpCircle size={32} className="text-blue-700 mb-4" />
            <span className="text-lg font-medium">How It Works</span>
          </Link>
          <Link to="/documents" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition duration-300">
            <Book size={32} className="text-blue-700 mb-4" />
            <span className="text-lg font-medium">Useful Documents</span>
          </Link>
          <Link to="/contact" className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition duration-300">
            <Mail size={32} className="text-blue-700 mb-4" />
            <span className="text-lg font-medium">Contact Us</span>
          </Link>
        </div>
      </section>
      
      {/* CSS for clipping the hero section */}
      <style jsx>{`
        .clip-triangle {
          clip-path: polygon(0 0, 100% 0, 0 100%);
          background-color: white;
        }
      `}</style>
    </div>
  );
};

export default Home;